/**
 * Price Collection Trigger Component
 * Allows users to manually trigger price collection from the Settings page
 */

import { useEffect, useState } from 'react'
import { useTriggerPriceCollection } from '@/services/api/prices'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { IconLoader2, IconRefresh } from '@tabler/icons-react'

/**
 * PriceCollectionTrigger component
 * 
 * Provides a button to manually trigger price collection with:
 * - Loading state during collection
 * - Success/error feedback via toast notifications
 * - Disabled state during operation
 * - Visual indicator that operation cannot be cancelled
 * 
 * @example
 * ```tsx
 * <PriceCollectionTrigger />
 * ```
 */
export function PriceCollectionTrigger() {
  const { mutate, isPending } = useTriggerPriceCollection()
  const [longRunningMessage, setLongRunningMessage] = useState(false)

  // Show "Đang xử lý..." message after 10 seconds
  useEffect(() => {
    if (isPending) {
      const timer = setTimeout(() => {
        setLongRunningMessage(true)
      }, 10000) // 10 seconds

      return () => clearTimeout(timer)
    } else {
      setLongRunningMessage(false)
    }
  }, [isPending])

  const handleTrigger = () => {
    mutate(undefined, {
      onSuccess: (data) => {
        const message = data.data.message || `Thu thập giá thành công: ${data.data.stored} bản ghi`
        toast.success(message)
      },
      onError: (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Đã xảy ra lỗi khi thu thập giá. Vui lòng thử lại sau.'
        toast.error(errorMessage)
      },
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Thu thập giá vàng</h2>
        <p className="text-muted-foreground mb-4">
          Kích hoạt thu thập giá vàng thủ công từ dịch vụ bên ngoài. Quá trình này có thể mất vài giây.
        </p>
      </div>

      <div className="space-y-3">
        {isPending && (
          <Progress value={100} className="w-full" />
        )}
        <div className="flex items-center gap-4">
          <Button
            onClick={handleTrigger}
            disabled={isPending}
            className="min-h-[44px]"
          >
            {isPending ? (
              <>
                <IconLoader2 className="h-4 w-4 mr-2 animate-spin" stroke={1.5} />
                Đang thu thập...
              </>
            ) : (
              <>
                <IconRefresh className="h-4 w-4 mr-2" stroke={1.5} />
                Thu thập giá ngay
              </>
            )}
          </Button>

          {isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <svg
                className="h-5 w-5 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>
                {longRunningMessage
                  ? 'Đang xử lý... Quá trình này không thể hủy bỏ.'
                  : 'Quá trình này không thể hủy bỏ.'}
              </span>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
